import { setScriptKey, getLicenseStatus, validatePackage, PackageMetadata, LicenseMessage } from '@progress/kendo-licensing';

let _parsedPackageMetadata: PackageMetadata = null;

try {
    _parsedPackageMetadata = Object.freeze(JSON.parse('$PACKAGE_METADATA'));
} catch {
    _parsedPackageMetadata = Object.freeze({
        name: '@progress/kendo-ui',
        productName: 'Kendo UI for jQuery',
        productCode: 'KENDOUICOMPLETE',
        productCodes: ["KENDOUICOMPLETE", "KENDOUI", "UIASPCORE", "KENDOMVC", "KENDOUIMVC"],
        redistributedBy: ['KENDOUI', 'UIASPCORE', 'KENDOMVC', 'KENDOUIMVC'],
        publishDate: 0,
        version: '0.0.0',
        licensingDocsUrl: 'https://www.telerik.com/kendo-jquery-ui/documentation/intro/installation/licensing/activation-errors-and-warnings'
    });
}

const TRUSTED_HOSTS = [/telerik\.com/, /progress\.com/, /stackblitz\.io/, /csb\.app/, /telerik\.io/];
const BANNER_FONT_FAMILY = 'system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, "Noto Sans", "Liberation Sans", sans-serif';

function applyStyles(el: HTMLElement, styles: { [key: string]: string }): void {
    Object.keys(styles).forEach(k => el.style.setProperty(k, styles[k]));
}

function sanitizeSvg(svg: string): string {
    return svg
        .replace(/\bfillRule\b/g, 'fill-rule')
        .replace(/\bclipRule\b/g, 'clip-rule')
        .replace(/\bstrokeWidth\b/g, 'stroke-width')
        .replace(/\bstrokeDasharray\b/g, 'stroke-dasharray')
        .replace(/\bstrokeLinecap\b/g, 'stroke-linecap')
        .replace(/\bstrokeLinejoin\b/g, 'stroke-linejoin');
}

function addWatermarkOverlay(el: HTMLElement): void {
    const watermark = document.createElement("div");

    applyStyles(watermark, {
        "position": "absolute",
        "width": "100%",
        "height": "100%",
        "pointer-events": "none",
        "top": "0",
        "left": "0",
        "right": "0",
        "bottom": "0"
    });

    const watermarkRoot = watermark.attachShadow({ mode: "closed" });
    const watermarkElement = document.createElement("div");

    applyStyles(watermarkElement, {
        "position": "absolute",
        "top": "0",
        "left": "0",
        "right": "0",
        "bottom": "0",
        "pointer-events": "none",
        "z-index": "101",
        "opacity": "0.12",
        "width": "100%",
        "height": "100%",
        "background-image": "url('data:image/png;base64,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')"
    });

    watermarkRoot.appendChild(watermarkElement);
    el.appendChild(watermark);

    const mo = new MutationObserver(function (e) {
        const record = e[0];

        if (record.removedNodes && Array.from(record.removedNodes).indexOf(watermark) > -1) {
            setTimeout(() => {
                el.appendChild(watermark);
            }, 100);
        }

        if (record.type === "attributes" && record.target === watermark) {
            setTimeout(() => {
                watermark.removeAttribute(record.attributeName);
            }, 100);
        }
    });

    mo.observe(el, { childList: true, attributes: true, subtree: true });
}

function createBannerElement(licenseMessage: LicenseMessage): HTMLElement {
    const banner = document.createElement('div');
    banner.setAttribute('id', 'k-license-banner');
    const iconSvg = sanitizeSvg(licenseMessage.notificationIcon.content || '');

    let bodyHTML = licenseMessage.notificationBody || '';
    const titleText = licenseMessage.notificationTitle || '';
    if (titleText && bodyHTML.startsWith(titleText)) {
        bodyHTML = bodyHTML.slice(titleText.length).trim();
    }

    const ctaHTML = licenseMessage.callToAction
        ? `<a id="k-license-cta" href="${licenseMessage.callToAction.link}" target="_blank" rel="noopener noreferrer">${licenseMessage.callToAction.message}</a>`
        : '';

    banner.innerHTML = `
        <span id="k-license-icon">${iconSvg}</span>
        <div id="k-license-content">
            <div id="k-license-title">${titleText}</div>
            <div id="k-license-body">${bodyHTML}</div>
        </div>
        <div id="k-license-actions">
            ${ctaHTML}
            <button id="k-license-close" title="Close">
                <svg width="20" height="20" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11.9309 3.1838C12.1754 2.93933 12.5712 2.93937 12.8157 3.1838C13.0601 3.4283 13.0601 3.82407 12.8157 4.06857L8.885 7.99923L12.8166 11.9309C13.0611 12.1754 13.0611 12.5721 12.8166 12.8166C12.5721 13.0611 12.1754 13.0611 11.9309 12.8166L7.99925 8.88497L4.06859 12.8166C3.8241 13.0611 3.42732 13.0611 3.18285 12.8166C2.93862 12.5721 2.93851 12.1753 3.18285 11.9309L7.11449 7.99923L3.18382 4.06857C2.93947 3.82413 2.93955 3.42829 3.18382 3.1838C3.42831 2.9393 3.82508 2.9393 4.06957 3.1838L7.99925 7.11349L11.9309 3.1838Z" fill="#212529"/>
                </svg>
            </button>
        </div>
    `;

    applyStyles(banner, {
        'display': 'flex',
        'position': 'fixed',
        'left': '50%',
        'transform': 'translateX(-50%)',
        'width': '100%',
        'border-left': '6px solid rgba(255, 192, 0, 1)',
        'border-top': '1px solid #00000029',
        'border-right': '1px solid #00000029',
        'border-bottom': '1px solid #00000029',
        'background-color': '#ffffff',
        'box-shadow': '0px 4px 5px 0px #0000000A, 0px 2px 4px 0px #00000008',
        'font-size': '14px',
        'font-weight': '400',
        'line-height': '20px',
        'color': '#1E1E1E',
        'justify-content': 'center',
        'z-index': '2000',
        'box-sizing': 'border-box'
    });

    const iconEl = banner.querySelector<HTMLElement>('#k-license-icon');
    applyStyles(iconEl, {
        'display': 'flex',
        'flex-shrink': '0'
    });

    const contentEl = banner.querySelector<HTMLElement>('#k-license-content');
    applyStyles(contentEl, {
        'display': 'flex',
        'flex-direction': 'column',
        'gap': '4px'
    });

    const titleEl = banner.querySelector<HTMLElement>('#k-license-title');
    applyStyles(titleEl, {
        'font-family': BANNER_FONT_FAMILY,
        'font-weight': '700',
        'font-size': '14px',
        'line-height': '142%',
        'font-style': 'Bold',
        'letter-spacing': '0px',
        'vertical-align': 'middle',
        'text-align': 'left'
    });

    const bodyEl = banner.querySelector<HTMLElement>('#k-license-body');
    applyStyles(bodyEl, {
        'font-family': BANNER_FONT_FAMILY,
        'font-weight': '400',
        'font-size': '14px',
        'line-height': '20px',
        'letter-spacing': '0px',
        'vertical-align': 'middle',
        'text-align': 'left'
    });

    const ctaEl = banner.querySelector<HTMLElement>('#k-license-cta');
    if (ctaEl) {
        applyStyles(ctaEl, {
            'display': 'inline-flex',
            'border': 'none',
            'border-radius': '4px',
            'background-color': '#eb0249',
            'color': '#ffffff',
            'padding': '4px 8px',
            'white-space': 'nowrap',
            'text-decoration': 'none',
            'font-family': BANNER_FONT_FAMILY,
            'cursor': 'pointer',
            'position': 'relative',
            'transition': 'background-color 0.2s ease-in-out',
            'outline': 'none'
        });
        ctaEl.addEventListener('mouseenter', function () {
            ctaEl.style.setProperty('background-color', '#b90138');
        });
        ctaEl.addEventListener('mouseleave', function () {
            ctaEl.style.setProperty('background-color', '#eb0249');
        });
    }

    const actionsEl = banner.querySelector<HTMLElement>('#k-license-actions');
    applyStyles(actionsEl, {
        'display': 'flex',
        'align-items': 'center',
        'gap': '16px'
    });

    const closeEl = banner.querySelector<HTMLElement>('#k-license-close');
    applyStyles(closeEl, {
        'display': 'inline-flex',
        'border': 'none',
        'border-radius': '4px',
        'padding': '4px',
        'background-color': 'transparent',
        'cursor': 'pointer',
        'outline': 'none',
        'transition': 'background-color 0.2s ease-in-out'
    });

    closeEl.addEventListener('mouseenter', function () {
        closeEl.style.setProperty('background-color', '#3d3d3d14');
    });

    closeEl.addEventListener('mouseleave', function () {
        closeEl.style.setProperty('background-color', 'transparent');
    });

    const mqMobile = window.matchMedia('(max-width: 499px)');
    const mqTablet = window.matchMedia('(max-width: 767px)');

    const applyResponsiveStyles = function () {
        const isMobile = mqMobile.matches;
        const isTablet = mqTablet.matches;
        const isDesktop = !isTablet;

        applyStyles(banner, {
            'top': isMobile || isTablet ? '0' : '16px',
            'max-width': isDesktop ? '768px' : 'none',
            'border-radius': isDesktop ? '6px' : '0',
            'flex-direction': isMobile ? 'column' : 'row',
            'align-items': isMobile ? 'flex-start' : 'center',
            'padding': isMobile ? '12px' : '0'
        });

        applyStyles(iconEl, {
            'align-self': isMobile ? 'flex-start' : 'center',
            'padding': isMobile ? '0 0 12px 0' : '9px 12px'
        });

        applyStyles(contentEl, {
            'flex': isMobile ? 'none' : '1',
            'padding': isMobile ? '0 0 12px 0' : '12px'
        });

        applyStyles(actionsEl, {
            'padding': isMobile ? '0' : '9px 12px',
            'margin-left': isMobile ? '0' : 'auto',
            'width': isMobile ? '100%' : 'auto'
        });

        if (isMobile) {
            applyStyles(closeEl, { 'position': 'absolute', 'top': '12px', 'right': '12px' });
        } else {
            applyStyles(closeEl, { 'position': 'relative' });
            closeEl.style.removeProperty('top');
            closeEl.style.removeProperty('right');
        }
    };

    applyResponsiveStyles();
    mqMobile.addEventListener('change', applyResponsiveStyles);
    mqTablet.addEventListener('change', applyResponsiveStyles);

    closeEl.addEventListener('click', function () {
        mqMobile.removeEventListener('change', applyResponsiveStyles);
        mqTablet.removeEventListener('change', applyResponsiveStyles);
        banner.hidden = true;
    });

    return banner;
}

function showBanner(licenseMessage: LicenseMessage): void {
    if (document.querySelector('#k-license-banner')) {
        return;
    }

    document.body.appendChild(createBannerElement(licenseMessage));
}

function shouldHideWatermarkAndBanner(): boolean {
    return TRUSTED_HOSTS.some(r => r.test(window.location.hostname));
}

const packageMetadata = _parsedPackageMetadata;

function addWatermarkOverlayAndBanner(el: HTMLElement): void {
    if (shouldHideWatermarkAndBanner()) {
        return;
    }

    const wrappersMetadata = (window as any)._wrappersMetadata as PackageMetadata;
    const { message } = getLicenseStatus(wrappersMetadata || packageMetadata);
    const isValid = validatePackage(wrappersMetadata || packageMetadata);

    if (!message && isValid) {
        return;
    }

    if (message.severity !== 'INFO') {
        addWatermarkOverlay(el);
    }

    showBanner(message);
}

export { setScriptKey, addWatermarkOverlayAndBanner, packageMetadata };
